import { useEffect, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import {
  BundlePricing,
  useGetSellableContentItemsQuery,
  useGetSubscriptionBundlesQuery,
  useUpdateSubscriptionBundlesMutation,
} from 'generated/graphql';
import {
  toSubscriptionPrograms,
  toSubscriptionBundles,
  mapToggledContent,
  ToggleBundleFreeTrialPayload,
  mapToggledFreeTrial,
  mapBundlesToInput,
  toFeatures,
  validatePlatformBundleSubset,
  ToggleSubscriptionBundlePayload,
} from './bundle.model.mapper';
import {
  AllFeatures,
  AllPrograms,
  SubscriptionBundle,
  Features,
  RawSubscriptionBundle,
  RawPrograms,
  AllContent,
  RawContentItem,
} from '../../common/bundle.types';
import toast from 'react-hot-toast';
import { BusinessModel } from '@playbook-technologies/common-enums';
import { bundleUpdateValidationToast } from '../../view/BundleUpdateValidationToast';
import {
  allowedContentTypes,
  toSellableContent,
} from '../../common/bundles.mapper';
import { useUser } from '@client/new/hooks/use-user';

export type BundlesModelData = {
  creatorId: number;
  subscriptionPrograms: AllPrograms;
  allFeatures: AllFeatures;
  sellableContent: AllContent;
  subscriptionBundles: SubscriptionBundle[];
  hasBundleSubscriptions: boolean;
};

type PayloadMap = {
  toggleBundleContent: ToggleSubscriptionBundlePayload;
  toggleBundleFreeTrial: ToggleBundleFreeTrialPayload;
  cancelEdit: null;
  saveChanges: null;
};

export type BundlesModelAction = Actions<PayloadMap>;
export type BundlesModelDispatcher = FSpread<PayloadMap>;

export type BundlesModel = BundlesModelData & {
  dispatch: BundlesModelDispatcher;
};
export const useSubscriptionBundlesModel = ({
  creatorId,
}: {
  creatorId: number;
}): Async<BundlesModel> => {
  const [subscriptionBundles, setSubscriptionBundles] = useState<
    SubscriptionBundle[]
  >([]);
  const [features, setFeatures] = useState<Features>([]);

  const { refetchInfluencer } = useUser(creatorId);

  const { data, loading, error, refetch } = useGetSubscriptionBundlesQuery({
    variables: {
      creatorId,
      bundlePricing: BundlePricing.Subscription,
    },
    fetchPolicy: 'network-only',
  });

  const { data: contentData } = useGetSellableContentItemsQuery({
    variables: {
      creatorId: String(creatorId),
    },
    fetchPolicy: 'network-only',
  });

  const [updateBundles] = useUpdateSubscriptionBundlesMutation();

  useEffect(() => {
    if (!loading && data?.bundle) {
      const rawSubscriptionBundles = (data?.bundle ||
        []) as RawSubscriptionBundle[];
      setSubscriptionBundles(toSubscriptionBundles(rawSubscriptionBundles));
    }
    if (!loading && data?.feature) {
      setFeatures(toFeatures(data?.feature));
    }
  }, [data, loading]);

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch bundle deatils',
    };
  }

  const rawContent = (contentData?.content?.contentItems || []).filter((item) =>
    allowedContentTypes.includes(item.type),
  ) as RawContentItem[];

  const initialSubscriptionBundles = (data?.bundle ||
    []) as RawSubscriptionBundle[];

  const saveChanges = () => {
    const input = mapBundlesToInput(subscriptionBundles);
    updateBundles({
      variables: {
        input: input,
      },
    })
      .then(() => {
        toast.success('Subscription offers were updated successfully');
      })
      .catch((e) => {
        toast.error(e.message);
      })
      .finally(() => {
        refetch();
        refetchInfluencer();
      });
  };

  const setBundleContentChange = (payload: ToggleSubscriptionBundlePayload) => {
    const mappedBundles = mapToggledContent(subscriptionBundles, payload);
    const hasError = validatePlatformBundleSubset(mappedBundles);
    if (hasError) {
      bundleUpdateValidationToast();
      return;
    }
    setSubscriptionBundles(mappedBundles);
  };

  const dispatch: BundlesModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as BundlesModelAction;
    switch (action.type) {
      case 'toggleBundleContent':
        setBundleContentChange(action.payload);
        return null;
      case 'toggleBundleFreeTrial':
        setSubscriptionBundles((prev) =>
          mapToggledFreeTrial(prev, action.payload),
        );
        return null;
      case 'cancelEdit':
        setSubscriptionBundles(
          toSubscriptionBundles(initialSubscriptionBundles),
        );
        return null;
      case 'saveChanges':
        saveChanges();
        return null;
    }
  };

  return {
    type: 'fetched',
    value: {
      dispatch,
      creatorId,
      subscriptionBundles,
      subscriptionPrograms: toSubscriptionPrograms(
        data?.creator?.programs as RawPrograms,
      ),
      sellableContent: rawContent ? toSellableContent(rawContent) : [],
      allFeatures: features,
      hasBundleSubscriptions:
        data?.creator?.businessModel === BusinessModel.Bundle,
    },
  };
};
